// PdfGenerate.js
import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
// import flyjac from "../../assets/svg/flyjac.png";
import logistics from "../../assets/svg/logistics.png";

// PDF styles
const styles = StyleSheet.create({
  page: { padding: 25, fontSize: 10, fontFamily: "Helvetica" },
  header: { flexDirection: "row", justifyContent: "space-between", marginBottom: 20 },
  logo: { width: 150, height: 30 },
  sectionTitle: { fontSize: 12, marginVertical: 10, fontWeight: "bold" },
  assumptionSectionTitle: { fontSize: 12, marginVertical: 5, fontWeight: "bold" },
  row: { flexDirection: "row", marginBottom: 10 },
  cellLabel: { width: "40%", fontWeight: "bold" },
  cellValue: { width: "60%" },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#ccc",
    marginBottom: 15,
  },
  tableRow: { flexDirection: "row" },
  tableHeaderCell: {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#ccc",
    paddingVertical: 8,
    paddingHorizontal: 4,
    fontSize: 9,
    fontWeight: "bold",
    backgroundColor: "#f2f2f2",
    textAlign: "center",
  },
  tableCell: {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#ccc",
    paddingVertical: 6,
    paddingHorizontal: 3,
    fontSize: 9,
    textAlign: "center",
    lineHeight: 1.5,
  },
  tableCellAlt: {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#ccc",
    paddingVertical: 6,
    paddingHorizontal: 3,
    fontSize: 9,
    textAlign: "center",
    backgroundColor: "#fafafa",
    lineHeight: 1.5,
  },
  tableTitle: { fontSize: 10, marginVertical: 6, fontWeight: "bold", color: "#222" },
});

const PdfGenerate = ({ summaryData, periods, projectDetails }) => {
  console.log("📄 Props received in PdfGenerate:", { summaryData, periods, projectDetails });
  const projectInfoList = [
    { label: "Customer", value: projectDetails?.customer },
    { label: "Ref No", value: projectDetails?.projectReference },
    { label: "Project Name", value: projectDetails?.projectName },
    { label: "Created Date", value: projectDetails?.modifieddate },
    { label: "Proposed Contract Length", value: projectDetails?.projectPeriod },
    { label: "Created By", value: projectDetails?.createdBy },
    { label: "Facility Location", value: projectDetails?.location },
    { label: "Customer Contact Name", value: projectDetails?.customerContactName },
    { label: "Payment Terms", value: projectDetails?.paymentTerms },
    { label: "Customer Contact Position", value: projectDetails?.customerContactPosition },
    { label: "Operation Area", value: projectDetails?.operationareas },
    { label: "Customer (Email/Phone)", value: projectDetails?.customerEmailPhone },
    { label: "Project Man Shift", value: projectDetails?.projectManShift },
    { label: "Scope of Work", value: projectDetails?.scope },
  ];

  // Calculate column widths based on periods
  const getColumnWidths = () => {
    const categoryWidth = 1.5;
    const periodWidth = 2;
    const totalWidth = 1.5;

    // For each period, we have two columns (Cost and MGT+WI)
    const periodColumnsWidth = periods.length * 2;

    return {
      category: categoryWidth,
      period: periodWidth / 2, // Split the period width between Cost and MGT+WI
      total: totalWidth,
      totalColumns: 1 + (periods.length * 2) + 1 // Category + (Periods * 2) + Total
    };
  };

  const renderSingleTable = (title, data) => {
    const { category, period, total, totalColumns } = getColumnWidths();

    // --- Compute grand totals ---
    const grandTotals = {
      periods: periods.map((_, j) =>
        data?.rows?.reduce((sum, row) => sum + (Number(row[`period${j + 1}`]) || 0), 0)
      ),
      overall: data?.rows?.reduce(
        (sum, row) =>
          sum +
          periods.reduce(
            (innerSum, _, j) => innerSum + (Number(row[`period${j + 1}`]) || 0),
            0
          ),
        0
      ),
    };

    return (
      <View wrap={false}>
        <Text style={styles.tableTitle}>{title}</Text>
        <View style={styles.table}>

          {/* -------- Header Row 1 -------- */}
          <View style={styles.tableRow}>
            <Text style={[styles.tableHeaderCell, { width: `${(category / totalColumns) * 100}%` }]}>
              Category
            </Text>
            {periods.map((_, i) => (
              <Text
                key={`period-${i}`}
                style={[
                  styles.tableHeaderCell,
                  { width: `${(period * 2 / totalColumns) * 100}%`, borderLeftWidth: i === 0 ? 1 : 0 }
                ]}
              >
                Period {i + 1}
              </Text>
            ))}
            <Text style={[styles.tableHeaderCell, { width: `${(total / totalColumns) * 100}%` }]}>
              Total
            </Text>
          </View>

          {/* -------- Header Row 2 -------- */}
          <View style={styles.tableRow}>
            <Text style={[styles.tableHeaderCell, { width: `${(category / totalColumns) * 100}%` }]} />
            {periods.map((_, i) => (
              <React.Fragment key={`subheader-${i}`}>
                <Text style={[styles.tableHeaderCell, { width: `${(period / totalColumns) * 100}%` }]}>
                  Cost
                </Text>
                <Text style={[styles.tableHeaderCell, { width: `${(period / totalColumns) * 100}%` }]}>
                  MGT+WI
                </Text>
              </React.Fragment>
            ))}
            <Text style={[styles.tableHeaderCell, { width: `${(total / totalColumns) * 100}%` }]} />
          </View>

          {/* -------- Data Rows -------- */}
          {data?.rows?.map((row, i) => {
            // ✅ Calculate row total dynamically
            const rowTotal = periods.reduce(
              (sum, _, j) => sum + (Number(row[`period${j + 1}`]) || 0),
              0
            );

            return (
              <View style={styles.tableRow} key={i}>
                {/* Category */}
                <Text
                  style={[
                    i % 2 === 0 ? styles.tableCell : styles.tableCellAlt,
                    { width: `${(category / totalColumns) * 100}%`, textAlign: "center" }
                  ]}
                >
                  {row.subcategory}
                </Text>

                {/* Period values */}
                {periods.map((_, j) => (
                  <React.Fragment key={`row-${i}-period-${j}`}>
                    {/* Cost */}
                    <Text
                      style={[
                        i % 2 === 0 ? styles.tableCell : styles.tableCellAlt,
                        { width: `${(period / totalColumns) * 100}%` }
                      ]}
                    >
                      {row[`period${j + 1}`] || 0}
                    </Text>
                    {/* Default MGT+WI = 0 */}
                    <Text
                      style={[
                        i % 2 === 0 ? styles.tableCell : styles.tableCellAlt,
                        { width: `${(period / totalColumns) * 100}%` }
                      ]}
                    >
                      0
                    </Text>
                  </React.Fragment>
                ))}

                {/* Row total */}
                <Text
                  style={[
                    i % 2 === 0 ? styles.tableCell : styles.tableCellAlt,
                    { width: `${(total / totalColumns) * 100}%` }
                  ]}
                >
                  {rowTotal.toFixed(2)}
                </Text>
              </View>
            );
          })}

          {/* -------- Grand Total Row -------- */}
          <View style={[styles.tableRow, { backgroundColor: "#eaeaea" }]}>
            <Text
              style={[
                styles.tableHeaderCell,
                { width: `${(category / totalColumns) * 100}%` }
              ]}
            >
              Grand Total
            </Text>

            {periods.map((_, j) => (
              <React.Fragment key={`grand-period-${j}`}>
                <Text
                  style={[styles.tableCell, { width: `${(period / totalColumns) * 100}%` }]}
                >
                  {grandTotals.periods[j].toFixed(2)}
                </Text>
                <Text
                  style={[styles.tableCell, { width: `${(period / totalColumns) * 100}%` }]}
                >
                  0
                </Text>
              </React.Fragment>
            ))}

            <Text
              style={[styles.tableCell, { width: `${(total / totalColumns) * 100}%` }]}
            >
              {grandTotals.overall.toFixed(2)}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined || val === "-") return "-";
    return Number(val).toFixed(2);
  };

  const renderComparisonTable = (title, withData, withoutData) => {
    const totalWithP1 = withData?.rows?.reduce((sum, r) => sum + (Number(r.period1) || 0), 0) || 0;
    const totalWithP2 = withData?.rows?.reduce((sum, r) => sum + (Number(r.period2) || 0), 0) || 0;

    const totalWithoutP1 = withoutData?.rows?.reduce((sum, r) => sum + (Number(r.period1) || 0), 0) || 0;
    const totalWithoutP2 = withoutData?.rows?.reduce((sum, r) => sum + (Number(r.period2) || 0), 0) || 0;

    return (
      <View wrap={false}>
        <Text style={styles.tableTitle}>{title}</Text>
        <View style={styles.table}>
          {/* -------- Header Row 1 -------- */}
          <View style={styles.tableRow}>
            <Text style={[styles.tableHeaderCell, { width: "30%" }]}>Category</Text>
            <Text style={[styles.tableHeaderCell, { width: "35%" }]} colSpan={2}>
              Period 1
            </Text>
            <Text style={[styles.tableHeaderCell, { width: "35%" }]} colSpan={2}>
              Period 2
            </Text>
          </View>

          {/* -------- Header Row 2 -------- */}
          <View style={styles.tableRow}>
            <Text style={[styles.tableHeaderCell, { width: "30%" }]} />
            <Text style={[styles.tableHeaderCell, { width: "17.5%" }]}>With</Text>
            <Text style={[styles.tableHeaderCell, { width: "17.5%" }]}>Without</Text>
            <Text style={[styles.tableHeaderCell, { width: "17.5%" }]}>With</Text>
            <Text style={[styles.tableHeaderCell, { width: "17.5%" }]}>Without</Text>
          </View>

          {/* -------- Data Rows -------- */}
          {withData?.rows?.map((row, i) => (
            <View style={styles.tableRow} key={`row-${i}`}>
              <Text style={[
                i % 2 === 0 ? styles.tableCell : styles.tableCellAlt,
                { width: "30%", textAlign: "center" }
              ]}>
                {row.subcategory}
              </Text>

              {/* Period 1 */}
              <Text style={[
                i % 2 === 0 ? styles.tableCell : styles.tableCellAlt,
                { width: "17.5%" }
              ]}>
                {row.period1}
              </Text>
              <Text style={[
                i % 2 === 0 ? styles.tableCell : styles.tableCellAlt,
                { width: "17.5%" }
              ]}>
                {formatNumber(withoutData?.rows?.[i]?.period1) || "-"}
              </Text>

              {/* Period 2 */}
              <Text style={[
                i % 2 === 0 ? styles.tableCell : styles.tableCellAlt,
                { width: "17.5%" }
              ]}>
                {row.period2}
              </Text>
              <Text style={[
                i % 2 === 0 ? styles.tableCell : styles.tableCellAlt,
                { width: "17.5%" }
              ]}>
                {formatNumber(withoutData?.rows?.[i]?.period2) || "-"}
              </Text>
            </View>
          ))}

          {/* -------- Total Row -------- */}
          <View style={[styles.tableRow, { backgroundColor: "#eaeaea" }]}>
            <Text style={[styles.tableHeaderCell, { width: "30%" }]}>Total</Text>
            <Text style={[styles.tableCell, { width: "17.5%" }]}>{formatNumber(totalWithP1)}</Text>
            <Text style={[styles.tableCell, { width: "17.5%" }]}>{formatNumber(totalWithoutP1)}</Text>
            <Text style={[styles.tableCell, { width: "17.5%" }]}>{formatNumber(totalWithP2)}</Text>
            <Text style={[styles.tableCell, { width: "17.5%" }]}>{formatNumber(totalWithoutP2)}</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderAssumptionsAndTerms = () => {

    const hasTerms = true;
    const hasAssumptions = true;

    return (
      <>
        <Text style={styles.sectionTitle}>TERMS & CONDITIONS</Text>
        {hasTerms ? (
          projectDetails.terms.map((term, index) => (
            <Text key={index} style={{ fontSize: 10, marginBottom: 5 }}>
              {index + 1}. {term.termLabel} - {term.termDescription}
            </Text>
          ))
        )
          :
          (
            <Text style={{ fontSize: 9, marginBottom: 10 }}>
              Standard project-related terms and conditions apply.
            </Text>
          )
        }

        <Text style={styles.sectionTitle}>ASSUMPTIONS & CONSIDERATIONS</Text>
        {hasAssumptions ? (
          projectDetails.assumptions.map((assumption, index) => (
            <Text key={index} style={{ fontSize: 10, marginBottom: 5 }}>
              {index + 1}. {assumption.assumptionLabel} - {assumption.assumptionDescription}
            </Text>
          ))
        ) : (
          <Text style={{ fontSize: 9 }}>
            All assumptions and considerations related to the project execution are listed here.
          </Text>
        )}
      </>
    );
  };


  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* Header */}
        <View style={styles.header} fixed>
          {/* <Image src={flyjac} style={styles.logo} /> */}
          <Image src={logistics} style={styles.logo} />
        </View>


        {/* Project Details */}
        <Text style={styles.sectionTitle}>PROJECT DETAILS</Text>
        {projectInfoList.map((item, idx) => (
          <View style={styles.row} key={idx}>
            <Text style={styles.cellLabel}>{item.label}:</Text>
            <Text style={styles.cellValue}>{item.value || "-"}</Text>
          </View>
        ))}

        {/* Cost Tables */}
        <Text style={styles.sectionTitle}>COST SUMMARY</Text>

        {renderSingleTable(
          "Estimated Cost (without buffer) + Management fee including Working capital interest (per month)",
          summaryData?.withoutBufferMonth
        )}

        {renderSingleTable(
          "Estimated Cost (without buffer) + Management fee including Working capital interest (per period)",
          summaryData?.withoutBufferPeriod
        )}

        {renderSingleTable(
          "Estimated Cost (with buffer) + Management fee including Working capital interest (per month)",
          summaryData?.withBufferMonth
        )}

        {renderSingleTable(
          "Estimated Cost (with buffer) + Management fee including Working capital interest (per period)",
          summaryData?.withBufferPeriod
        )}

        {renderComparisonTable(
          "Cost Comparison: With/Without buffer (per month)",
          summaryData?.withBufferMonth,
          summaryData?.withoutBufferMonth
        )}

        {renderComparisonTable(
          "Cost Comparison: With/Without buffer (per period)",
          summaryData?.withBufferPeriod,
          summaryData?.withoutBufferPeriod
        )}

        {/* Assumptions and Terms */}
        {renderAssumptionsAndTerms()}

      </Page>
    </Document>
  );
};

export default PdfGenerate;
